/**
 * PublicHero - light page header for the public site ("Data Product" v3).
 * White canvas with a barely-there green radial wash, an eyebrow pill
 * (optional live dot), a heavy grotesk headline with one green accent word,
 * a muted subtitle and an optional actions row / right-hand aside.
 *
 * The prop API is kept from earlier versions ({ media, panel } are accepted
 * but no longer render a photographic/gradient treatment - the v3 site is
 * light and product-led).
 */

import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { useThemeMode, displaySx } from '../../context/ThemeContext';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

function PublicHero({ eyebrow, title, accent, subtitle, actions, aside, media, topBar, panel = false, live = false, compact = false }) {
  const { palette } = useThemeMode();
  const reduce = useReducedMotion();
  const hasAside = Boolean(aside);
  // The navbar is sticky (in-flow), so the header needs no clearance padding.
  const contentPt = { xs: 6, md: compact ? 6 : 8 };

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        bgcolor: palette.bg,
        borderBottom: `1px solid ${palette.divider}`,
      }}
    >
      {/* Barely-there green wash, top-right */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
          background: 'radial-gradient(720px 360px at 88% 0%, rgba(34,197,94,0.06), transparent 70%)',
        }}
      />

      {/* Optional top strip slot (rarely used - the site ticker lives above the header) */}
      {topBar && (
        <Box sx={{ position: 'relative', zIndex: 2 }}>{topBar}</Box>
      )}

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: contentPt, pb: { xs: 6, md: compact ? 7 : 9 } }}>
        <Grid container spacing={{ xs: 5, md: 7 }} alignItems="center">
          <Grid item xs={12} md={hasAside ? 6 : 12}>
            <motion.div variants={fadeUp} initial={reduce ? 'show' : 'hidden'} animate="show">
              {eyebrow && (
                <Box
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 1,
                    px: 1.5, py: 0.65, mb: 3,
                    bgcolor: palette.surface, border: `1px solid ${palette.divider}`,
                    borderRadius: 999,
                  }}
                >
                  {live && (
                    <Box
                      sx={{
                        width: 7, height: 7, borderRadius: '50%', bgcolor: palette.accentStrong,
                        animation: reduce ? 'none' : 'fgPulse 2s infinite',
                        '@keyframes fgPulse': {
                          '0%': { boxShadow: `0 0 0 0 ${palette.accentStrong}59` },
                          '70%': { boxShadow: '0 0 0 6px transparent' },
                          '100%': { boxShadow: '0 0 0 0 transparent' },
                        },
                      }}
                    />
                  )}
                  <Typography component="span" sx={{ fontSize: '0.78rem', fontWeight: 600, color: palette.textSecondary }}>
                    {eyebrow}
                  </Typography>
                </Box>
              )}

              <Typography
                component="h1"
                sx={{
                  ...displaySx,
                  fontSize: hasAside
                    ? { xs: '2.1rem', sm: '2.5rem', md: '2.9rem', lg: '3.1rem' }
                    : displaySx.fontSize,
                  color: palette.text,
                  maxWidth: hasAside ? 'none' : 760,
                }}
              >
                {title}
                {accent && (
                  <Box component="span" sx={{ color: palette.accent }}>
                    {title ? ' ' : ''}
                    {accent}
                  </Box>
                )}
              </Typography>

              {subtitle && (
                <Typography sx={{ mt: 2.25, color: palette.textSecondary, fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.6, maxWidth: 520 }}>
                  {subtitle}
                </Typography>
              )}

              {actions && <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.75 }}>{actions}</Box>}
            </motion.div>
          </Grid>

          {hasAside && (
            <Grid item xs={12} md={6}>
              <motion.div initial={reduce ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}>
                {aside}
              </motion.div>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
}

export default PublicHero;
